/**
 * addStoreManager — POST /api/retailers/store-managers/. Records a
 * manager-of-record entry for a store. Fired during checkout when the
 * rep types a new manager name. No dependency.
 */

import type { WriteOp, SyncResult } from '../types';
import { STORES, get, put } from '../../utils/indexedDB';
import { BASE_URL, jsonHeaders, classifyResponse, networkErrorResult } from '../httpHelpers';

export interface AddStoreManagerInput {
  storeId: number;
  managerName: string;
}

export interface AddStoreManagerResponse {
  id?: number;
}

interface StoreManagersSingleton {
  key: string;
  managers: Array<{ localId: string; managerName: string; serverId?: number }>;
}

function mgrKey(storeId: number): string {
  return `storeManagers:${storeId}`;
}

export const addStoreManagerOp: WriteOp<
  AddStoreManagerInput,
  AddStoreManagerInput,
  AddStoreManagerResponse
> = {
  kind: 'addStoreManager',

  async applyLocal(input, localId) {
    const key = mgrKey(input.storeId);
    const existing =
      (await get<StoreManagersSingleton>(STORES.VISIT_SINGLETONS, key)) ||
      ({ key, managers: [] } as StoreManagersSingleton);
    existing.managers.push({ localId, managerName: input.managerName });
    await put(STORES.VISIT_SINGLETONS, existing);
  },

  serialize(input, localId) {
    return {
      action: 'addStoreManager',
      endpoint: '/api/retailers/store-managers/',
      method: 'POST',
      payload: input,
      priority: 70,
      maxRetries: 10,
      dependsOn: null,
      relatedTaskUUID: localId,
      idempotencyKey: localId,
    };
  },

  async sync(row): Promise<SyncResult> {
    const input = row.payload as AddStoreManagerInput;

    let response: Response;
    try {
      response = await fetch(`${BASE_URL}${row.endpoint}`, {
        method: row.method,
        headers: jsonHeaders(row.idempotencyKey),
        body: JSON.stringify({ store: input.storeId, manager_name: input.managerName }),
      });
    } catch (err) {
      return networkErrorResult(err);
    }

    // #2464 belt: a duplicate (store, manager_name) IS the desired end state — the
    // history row already exists. An older/lagging server 400s with a unique-set
    // error; treat that as success so this best-effort checkout save never strands
    // the queue in Sync Issues (image005). The server fix makes create() idempotent.
    if (response.status === 400) {
      const text = await response.clone().text().catch(() => '');
      if (/must make a unique set|unique set|already exists/i.test(text)) {
        return { status: 'success', retriable: false };
      }
    }

    const classified = await classifyResponse(response);
    if (classified) return classified;

    const data = await response.json().catch(() => ({}));
    return {
      status: 'success',
      serverId: typeof data?.id === 'number' ? data.id : undefined,
      retriable: false,
    };
  },

  async reconcile(row, response) {
    if (typeof response.id !== 'number') return;
    const input = row.payload as AddStoreManagerInput;
    const localId = row.relatedTaskUUID;
    if (!localId) return;
    const key = mgrKey(input.storeId);
    const existing = await get<StoreManagersSingleton>(STORES.VISIT_SINGLETONS, key);
    if (!existing) return;
    const match = existing.managers.find((m) => m.localId === localId);
    if (match) match.serverId = response.id;
    await put(STORES.VISIT_SINGLETONS, existing);
  },

  dependsOn() {
    return null;
  },
};

export type AddStoreManagerOp = typeof addStoreManagerOp;
